import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import client from '../../../lib/feathers';
import { cropTileStyle } from '../../../lib/cropTile';

// Inspect + hand-adjust an attachment's crop box. The box is normalized
// {x,y,w,h} on `attachment.cropBox` — what the client square-crops to in the
// wine list. Since that render is ALWAYS a square cover-crop, the editor locks
// the rectangle to a square (in image pixels) so what you draw is exactly what
// shows. Existing non-square boxes (the v1 whole-bottle detections) are
// converted to the equivalent square on load so the editor opens showing the
// current thumbnail.

const MIN_PX = 28; // smallest square edge while dragging

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const isFullFrame = (b) => b && b.w >= 0.999 && b.h >= 0.999 && (b.x || 0) <= 0.001 && (b.y || 0) <= 0.001;
const hasUsableBox = (b) => b && b.w > 0 && b.h > 0 && !isFullFrame(b);

const FULL_BOX = { x: 0, y: 0, w: 1, h: 1 };

// The square (in display px) that a cover-crop of `box` actually shows: centered
// on the box, side = the box's larger pixel dimension, clamped to the frame.
const coverSquare = (box, W, H) => {
  let s = Math.min(Math.max(box.w * W, box.h * H), W, H);
  const cx = (box.x + box.w / 2) * W;
  const cy = (box.y + box.h / 2) * H;
  const L = clamp(cx - s / 2, 0, W - s);
  const T = clamp(cy - s / 2, 0, H - s);
  return { x: L / W, y: T / H, w: s / W, h: s / H };
};

// Starting square when there's no usable box: ~60% of the short edge, lower-center.
const defaultSquare = (W, H) => {
  const s = Math.min(W, H) * 0.6;
  const L = clamp(W / 2 - s / 2, 0, W - s);
  const T = clamp(H * 0.6 - s / 2, 0, H - s);
  return { x: L / W, y: T / H, w: s / W, h: s / H };
};

const HANDLES = ['nw', 'ne', 'sw', 'se'];
const handlePos = {
  nw: { left: 0, top: 0, cursor: 'nwse-resize' },
  ne: { left: 1, top: 0, cursor: 'nesw-resize' },
  sw: { left: 0, top: 1, cursor: 'nesw-resize' },
  se: { left: 1, top: 1, cursor: 'nwse-resize' },
};

const AttachmentCropEditor = ({ attachment, onClose, onSaved }) => {
  const role = useSelector((s) => s.auth.user.role);
  const url = `${process.env.REACT_APP_ATTACHMENT_URL}/${attachment.path}`;

  const storedDims = (attachment.width && attachment.height)
    ? { w: attachment.width, h: attachment.height } : null;
  // The box is normalized in the UNTRIMMED frame (the cropbox worker measures the
  // raw S3 object; the client renders the `?w=` untrimmed image). Stored dims are
  // that frame, so seed from them. When missing, load the NO-PARAM original —
  // never a `?w=` variant (width-only resize → squashed aspect) or `f=inside` (the
  // CDN trims whitespace → a different, tall aspect than what's displayed here).
  const [dims, setDims] = useState(storedDims || { w: 0, h: 0 });
  // Dims to persist with the box on save (fills a missing stored value).
  const [natural, setNatural] = useState(storedDims);
  const [box, setBox] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (natural) return undefined; // stored dims present — nothing to load
    const img = new Image();
    img.onload = () => setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = url; // no-param original = true untrimmed aspect
    return () => { img.onload = null; };
  }, [url, natural]);

  // `natural` is authoritative for the stage/box math (true untrimmed ratio).
  useEffect(() => {
    if (natural && natural.w && natural.h) setDims(natural);
  }, [natural]);

  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const initedRef = useRef(false);

  // Fit the image inside a fixed viewport, preserving aspect ratio, so the
  // overlay maps directly to the displayed image (no letterboxing).
  const maxW = 360;
  const maxH = 520;
  let dispW = maxW;
  let dispH = maxH;
  if (dims.w && dims.h) {
    const scale = Math.min(maxW / dims.w, maxH / dims.h);
    dispW = Math.round(dims.w * scale);
    dispH = Math.round(dims.h * scale);
  }

  // Initialize the (square) box once the display dimensions are known.
  useEffect(() => {
    if (!dims.w || !dims.h || initedRef.current) return;
    setBox(hasUsableBox(attachment.cropBox)
      ? coverSquare(attachment.cropBox, dispW, dispH)
      : defaultSquare(dispW, dispH));
    initedRef.current = true;
  }, [dims, attachment.cropBox, dispW, dispH]);

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const { W, H } = d;
    if (d.mode === 'move') {
      const wPx = d.startBox.w * W;
      const hPx = d.startBox.h * H;
      const L = clamp(d.startL + (e.clientX - d.startX), 0, W - wPx);
      const T = clamp(d.startT + (e.clientY - d.startY), 0, H - hPx);
      setBox({ x: L / W, y: T / H, w: d.startBox.w, h: d.startBox.h });
      return;
    }
    // Square resize anchored on the opposite corner.
    const px = clamp(e.clientX - d.rectLeft, 0, W);
    const py = clamp(e.clientY - d.rectTop, 0, H);
    const maxX = d.handle.includes('e') ? W - d.anchorX : d.anchorX;
    const maxY = d.handle.includes('s') ? H - d.anchorY : d.anchorY;
    let side = Math.max(Math.abs(px - d.anchorX), Math.abs(py - d.anchorY));
    side = clamp(side, MIN_PX, Math.min(maxX, maxY));
    const newL = d.handle.includes('e') ? d.anchorX : d.anchorX - side;
    const newT = d.handle.includes('s') ? d.anchorY : d.anchorY - side;
    setBox({ x: newL / W, y: newT / H, w: side / W, h: side / H });
  };

  const onPointerUp = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  };

  const startDrag = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!box) return;
    const rect = stageRef.current.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;
    const L = box.x * W;
    const T = box.y * H;
    const R = (box.x + box.w) * W;
    const B = (box.y + box.h) * H;
    // Anchor = the corner opposite the dragged handle.
    const anchor = {
      se: { anchorX: L, anchorY: T },
      sw: { anchorX: R, anchorY: T },
      ne: { anchorX: L, anchorY: B },
      nw: { anchorX: R, anchorY: B },
    }[mode] || {};
    dragRef.current = {
      mode: mode === 'move' ? 'move' : 'resize',
      handle: mode,
      startBox: box,
      startL: L,
      startT: T,
      startX: e.clientX,
      startY: e.clientY,
      rectLeft: rect.left,
      rectTop: rect.top,
      W,
      H,
      ...anchor,
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const persist = async (newBox) => {
    setSaving(true);
    try {
      const patch = { cropBox: newBox };
      if (newBox) {
        // Hand-edited box: tag 'manual' so the detector re-run never overwrites it.
        patch.cropBoxSource = 'manual';
        // Persist the served (true-aspect) dims so the thumbnail crops correctly.
        // Overwrite when missing OR when the stored aspect disagrees with the
        // served image (self-heals a wrong stored value like 1184x4342 → 1184x1080).
        if (natural && natural.w && natural.h) {
          const storedAspect = (attachment.width && attachment.height)
            ? attachment.height / attachment.width : null;
          const servedAspect = natural.h / natural.w;
          const aspectOff = storedAspect == null
            || Math.abs(storedAspect - servedAspect) / servedAspect > 0.02;
          if (aspectOff) {
            patch.width = natural.w;
            patch.height = natural.h;
          }
        }
      } else {
        // Clear → put it back in the detector queue (worker selects version < N).
        patch.cropBoxSource = null;
        patch.cropBoxVersion = null;
      }
      await client.service(`${role}/attachment`).patch(attachment.id, patch);
      toast.success(newBox === null ? 'Crop cleared — will re-detect' : 'Crop saved');
      if (onSaved) {
        onSaved({
          ...attachment,
          cropBox: newBox,
          width: patch.width || attachment.width,
          height: patch.height || attachment.height,
        });
      }
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save crop');
      setSaving(false);
    }
  };

  const resetBox = () => setBox(hasUsableBox(attachment.cropBox)
    ? coverSquare(attachment.cropBox, dispW, dispH)
    : defaultSquare(dispW, dispH));

  const aspW = natural ? natural.w : dims.w;
  const aspH = natural ? natural.h : dims.h;
  const previewCrop = box ? cropTileStyle({ width: aspW, height: aspH, cropBox: box }, 120) : null;
  const pct = (n) => `${(n * 100).toFixed(2)}%`;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-auto p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-lg font-medium text-gray-900">Crop box</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faXmark} size="lg" />
          </button>
        </div>

        <div className="p-5 flex flex-col md:flex-row gap-6">
          {/* Drag stage */}
          <div className="flex-shrink-0 mx-auto">
            <div
              ref={stageRef}
              className="relative select-none bg-gray-100 rounded overflow-hidden"
              style={{ width: dispW, height: dispH, touchAction: 'none' }}
            >
              <img
                src={`${url}?w=${Math.min(800, dispW * 2)}`}
                alt=""
                draggable={false}
                onLoad={(e) => {
                  if (!dims.w || !dims.h) {
                    setDims({ w: e.target.naturalWidth, h: e.target.naturalHeight });
                  }
                }}
                style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
              />

              {box && (
                <div
                  onPointerDown={startDrag('move')}
                  style={{
                    position: 'absolute',
                    left: pct(box.x),
                    top: pct(box.y),
                    width: pct(box.w),
                    height: pct(box.h),
                    border: '2px solid #fff',
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                    cursor: 'move',
                  }}
                >
                  {HANDLES.map((h) => (
                    <div
                      key={h}
                      onPointerDown={startDrag(h)}
                      style={{
                        position: 'absolute',
                        left: `calc(${handlePos[h].left * 100}% - 7px)`,
                        top: `calc(${handlePos[h].top * 100}% - 7px)`,
                        width: 14,
                        height: 14,
                        background: '#fff',
                        border: '1px solid #4b5563',
                        borderRadius: 3,
                        cursor: handlePos[h].cursor,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">Drag to move; corners resize (locked square).</p>
          </div>

          {/* Preview + actions */}
          <div className="flex-1 space-y-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">List thumbnail</p>
              <div className="relative w-[120px] h-[120px] bg-gray-100 rounded overflow-hidden border">
                {previewCrop ? (
                  <img src={`${url}?w=${previewCrop.srcW}`} alt="" style={previewCrop.style} />
                ) : (
                  <img src={`${url}?w=240&h=240`} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">Exactly what the wine list shows.</p>
            </div>

            {box && (
              <div className="text-xs text-gray-500 font-mono">
                x {box.x.toFixed(3)} · y {box.y.toFixed(3)} · w {box.w.toFixed(3)} · h {box.h.toFixed(3)}
              </div>
            )}

            <div className="space-y-2 pt-2">
              <button
                disabled={saving || !box}
                onClick={() => persist(box)}
                className="w-full px-3 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                Save crop
              </button>
              <button
                disabled={saving}
                onClick={resetBox}
                className="w-full px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Reset
              </button>
              <button
                disabled={saving}
                onClick={() => persist(FULL_BOX)}
                className="w-full px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                title="Show the whole image, no crop"
              >
                No crop (full frame)
              </button>
              <button
                disabled={saving}
                onClick={() => persist(null)}
                className="w-full px-3 py-2 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                title="Clear the box so the detector re-runs on it"
              >
                Clear &amp; re-detect
              </button>
              <a
                href={`${url}?f=inside`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-xs text-primary hover:underline pt-1"
              >
                Open full image
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentCropEditor;
